import React, { useContext } from "react";
import {
    ConfirmDialog,
    createFailure,
    createListTableColumns,
    createValidationResult,
    ensure,
    FailureSeverity,
    FormValidationSummary,
    ShimmeredDetailsList,
    useNotifications,
    usePerformMultipleActions,
    ValidationFailure,
    Text,
    renderTextWithTooltip
} from "sprint-common-web";

import { useFileRecordApi } from "../../../../../common/api/master-api/file-records/useFileRecordApi";
import { renderActionStatus } from "../../../../../common/utils/renderActionStatus";
import { FileRecordListingContext } from "../../context/FileRecordListingContext";
import { ApproveNotificationsItem, ProcessApproveNotificationsItem } from "./ApproveNotificationsItem";

interface ApproveNotificationsDialogProps {
    isOpen: boolean;
    onClose(): void;
    selectedItems: ApproveNotificationsItem[];
}

export const ApproveFileRecordNotificationsDialog: React.VFC<ApproveNotificationsDialogProps> = ({
    isOpen,
    onClose,
    selectedItems
}) => {
    const { approveFileRecordNotifications } = useFileRecordApi();

    const { approveNotifications: approveNotificationsInContext } = useContext(FileRecordListingContext);

    const { success, error } = useNotifications();

    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions<ApproveNotificationsItem>({
        isAvailable: (item) => {
            return !item.isNotificationApproved;
        },
        selectedItems
    });

    const failure = validateDiscardedItems(items, selectedItems.length);

    const validationResult = createValidationResult([failure]);

    const handleApprove = async () => {
        const results = await approveAllItems();

        if (results.every(({ status }) => status === "fulfilled")) {
            handleClose();

            success("Notifications approved");
        } else error("Notifications approve failed");
    };

    const approveAllItems = async () => {
        return performAllItems(async (item) => {
            await approveFileRecordNotifications({ fileRecordId: item.id });

            approveNotificationsInContext(item.id);
        });
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <ConfirmDialog
            title="Approve buzz notifications"
            text="Are you sure you want to approve buzz notifications for given file records?"
            hidden={!isOpen}
            isBlocking={isActionInProgress}
            width={"65%"}
            contentHeight={500}
            confirm={{ onClick: handleApprove, disabled: !items.length }}
            cancel={{ onClick: handleClose }}
        >
            <FormValidationSummary validationResult={validationResult} />
            {!!items.length && <ApproveNotificationsSummary items={items} />}
        </ConfirmDialog>
    );
};

const discardedOrdersInfoMessage = `File record with already approved buzz notifications have been discarded`;

interface ApproveNotificationsSummaryProps {
    items: ApproveNotificationsItem[];
}

const ApproveNotificationsSummary: React.VFC<ApproveNotificationsSummaryProps> = ({
    items
}: ApproveNotificationsSummaryProps) => {
    const columns = createListTableColumns<ProcessApproveNotificationsItem>([
        {
            type: "basic",
            key: "deliveryFileName",
            name: "Delivery file name",
            minWidth: 80,
            maxWidth: 250,
            onRender: renderTextWithTooltip
        },
        {
            type: "basic",
            key: "filePath",
            name: "File path",
            minWidth: 60,
            maxWidth: 300,
            onRender: renderFilePath
        },
        {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 60,
            maxWidth: 300
        },
        {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 60
        },
        {
            type: "basic",
            key: "processStatus",
            name: "Process status",
            minWidth: 100,
            onRender: renderActionStatus
        }
    ]);

    return <ShimmeredDetailsList columns={columns} items={items} layoutMode="justified" />;
};

const validateDiscardedItems = (
    items: ApproveNotificationsItem[],
    selectedItemsCount: number
): ValidationFailure | void => {
    if (!items.length) {
        return createFailure(
            `${discardedOrdersInfoMessage}. Nothing to deliver.`,
            "no-orders-to-deliver",
            FailureSeverity.Error
        );
    } else if (items.length < selectedItemsCount) {
        return createFailure(discardedOrdersInfoMessage, "orders-discarded-from-delivery", FailureSeverity.Warning);
    }
};

const renderFilePath = (item?: ApproveNotificationsItem) => {
    ensure(item);

    return <Text variant="small" tooltip={item.filePath} value={item.filePath ?? "-"} />;
};
